import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MindARThree } from 'mind-ar/dist/mindar-face-three.prod.js';
import config from '../../config/config';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';
import './Necklace.css';

function Necklace() {
  const { sku } = useParams();
  const category = 'necklaces';
  const containerRef = useRef(null);
  const mindarThreeRef = useRef(null);
  const sceneRef = useRef(null);
  const anchorRef = useRef({
    necklaceAnchor: null,
  });
  const modelsRef = useRef({
    necklace: null,
  });
  const [selectedFilter, setSelectedFilter] = useState('none');

  // Cartoon shader
  const cartoonMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 1).normalize() },
      toonLevel: { value: 4.0 },
      edge: { value: 0.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 lightDirection;
      uniform float toonLevel;
      uniform float edge;
      
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        float intensity = dot(vNormal, lightDirection);
        intensity = ceil(intensity * toonLevel) / toonLevel;
        
        // Edge detection
        float edgeIntensity = length(fwidth(vNormal));
        float isEdge = edgeIntensity > edge ? 0.0 : 1.0;
        
        vec3 toonColor = vec3(intensity * isEdge);
        gl_FragColor = vec4(toonColor, 0.5);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  }), []);

  // Load and apply face texture
  const texture = useMemo(() => new THREE.TextureLoader().load('/assets/thumbnails/face-mask.png'), []);

  // Initialize scene and lights
  const initializeScene = (mindarThree) => {
    const { renderer, scene, camera } = mindarThree;
    
    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);

    return { renderer, scene, camera };
  };

  // Load necklace model
  const loadNecklaceModel = useCallback(async () => {
    const loader = new GLTFLoader();
    try {
      // Try loading from CDN first if SKU is provided
      if (sku) {
        try {
          const cdnPath = `${config.cdnUrl}/models/${category}/${sku}/scene.glb`;
          const gltf = await loader.loadAsync(cdnPath);
          const model = gltf.scene;
          
          // Store initial scale and position for reference
          model.userData.baseScale = { 
            x: model.scale.x, 
            y: model.scale.y, 
            z: model.scale.z 
          };
          model.userData.basePosition = { 
            x: model.position.x, 
            y: model.position.y, 
            z: model.position.z 
          };
          
          return model;
        } catch (cdnError) {
          console.warn('Failed to load from CDN, falling back to default model:', cdnError);
        }
      }
      
      // Load default model if SKU is not provided or CDN loading failed
      const gltf = await loader.loadAsync(config.defaultNecklaceModelPath);
      const model = gltf.scene;
      
      // Store initial scale and position for reference
      model.userData.baseScale = { 
        x: model.scale.x, 
        y: model.scale.y, 
        z: model.scale.z 
      };
      model.userData.basePosition = { 
        x: model.position.x, 
        y: model.position.y, 
        z: model.position.z 
      };
      
      return model;
    } catch (error) {
      console.error('Error loading necklace model:', error);
      return null;
    }
  }, [sku, category]);

  // Create necklace instance
  const createNecklace = (baseModel, rotation, position) => {
    if (!baseModel) return null;
    
    const necklace = baseModel.clone();
    necklace.traverse((node) => {
      if (node.isMesh) {
        node.material.depthTest = true;
        node.material.depthWrite = true;
        node.renderOrder = 1;
      }
    });
    
    necklace.rotation.set(...rotation);
    necklace.position.set(...position);
    necklace.scale.set(0.5, 0.5, 0.5);
    
    return necklace;
  };

  // Initialize all models and scene
  const initializeModels = useCallback(async (mindarThree) => {
    // Create anchors
    const necklaceAnchor = mindarThree.addAnchor(152);
    
    anchorRef.current = {
      necklaceAnchor,
    };

    // Load models
    const necklaceBase = await loadNecklaceModel();

    if (necklaceBase) {
      const necklace = createNecklace(necklaceBase, [-0.1, 0, 0], [-0.05, -0.5, -0.2]);
      
      if (necklace) {
        necklaceAnchor.group.add(necklace);
        modelsRef.current.necklace = necklace;
      }
    }
  }, [loadNecklaceModel]);  

  useEffect(() => {
    let mounted = true;
    const currentMindarRef = mindarThreeRef;
    const currentSceneRef = sceneRef;
    const currentModelsRef = modelsRef.current;

    const setup = async () => {
      if (!mounted) return;

      const mindarThree = new MindARThree({
        container: containerRef.current,
        filterMinCF: 0.001,
        filterBeta: 1,
      });

      const { renderer, scene, camera } = initializeScene(mindarThree);
      await initializeModels(mindarThree);
      
      if (mounted) {
        currentMindarRef.current = mindarThree;
        currentSceneRef.current = { renderer, scene, camera };
        
        // Auto-start the AR experience
        await mindarThree.start();

        renderer.setAnimationLoop(() => {
          renderer.render(scene, camera);
        });
      }
    };

    setup();

    return () => {
      mounted = false;
      if (currentMindarRef.current) {
        currentMindarRef.current.stop();
        currentMindarRef.current.renderer.setAnimationLoop(null);

        if (currentSceneRef.current) {
          const { scene, renderer } = currentSceneRef.current;
          
          // Clear models from scene
          Object.values(currentModelsRef).forEach(model => {
            if (model && model.parent) {
              model.parent.remove(model);
            }
          });

          // Render empty scene and clear
          renderer.render(scene, currentSceneRef.current.camera);
          renderer.clear();
        }
      }
    };
  }, [initializeModels]);

  useEffect(() => {
    if (sceneRef.current && mindarThreeRef.current) {
      const mindarThree = mindarThreeRef.current;
      const { renderer, scene, camera } = sceneRef.current;
      
      // Remove existing face mesh if any
      const existingFaceMesh = scene.children.find(child => child.isFaceMesh);
      if (existingFaceMesh) {
        scene.remove(existingFaceMesh);
      }

      // Add new face mesh based on selected filter
      if (selectedFilter === 'none') {
        renderer.render(scene, camera);
      } else if (selectedFilter === 'cartoon') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material = cartoonMaterial;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      } else if (selectedFilter === 'animal') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material.map = texture;
        faceMesh.material.transparent = true;
        faceMesh.material.needsUpdate = true;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      }
    }
  }, [selectedFilter, cartoonMaterial, texture]);

  // Add function to get current renderer and scene info
  const getRendererInfo = () => {
    return {
      mindarThree: mindarThreeRef.current,
      models: modelsRef.current
    };
  };

  return (
    <div className="container">
      <div className="ar-container" ref={containerRef}>
        <video className="video"></video>
      </div>
      <div className="control-panel">
        <FilterAndCaptureButtons 
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          getRendererInfo={getRendererInfo}
        />
      </div>
    </div>
  );
}

export default Necklace;